import { useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Like useState but persisted in URL search params.
 * Uses a shared ref to batch multiple updates in the same tick.
 */

const pendingUpdates = new Map<string, string | null>();
let flushScheduled = false;

export function useSearchParamState(
  key: string,
  defaultValue: string
): [string, (v: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(key) ?? defaultValue;

  const defaultRef = useRef(defaultValue);
  defaultRef.current = defaultValue;

  const setValue = useCallback(
    (newValue: string) => {
      if (newValue === defaultRef.current) {
        pendingUpdates.set(key, null); // delete
      } else {
        pendingUpdates.set(key, newValue);
      }

      if (!flushScheduled) {
        flushScheduled = true;
        // Use microtask to batch all sync updates in the same tick
        Promise.resolve().then(() => {
          flushScheduled = false;
          const updates = new Map(pendingUpdates);
          pendingUpdates.clear();

          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              for (const [k, v] of updates) {
                if (v === null) {
                  next.delete(k);
                } else {
                  next.set(k, v);
                }
              }
              return next;
            },
            { replace: true }
          );
        });
      }
    },
    [key, setSearchParams]
  );

  return [value, setValue];
}

export function useSearchParamNumber(
  key: string,
  defaultValue: number
): [number, (v: number) => void] {
  const [raw, setRaw] = useSearchParamState(key, String(defaultValue));
  const value = parseInt(raw, 10) || defaultValue;
  const setValue = useCallback(
    (v: number) => setRaw(String(v)),
    [setRaw]
  );
  return [value, setValue];
}
